/**
 * Tool authoring DOCS dialog (ADR-0033 S4) — a small "how to write a tool" guide + the
 * list of sandbox libraries WITH a one-line description each, IT/EN (from `lib/toolDocs`).
 * Opened by the "Documentazione" button in the tool IDE. The library list reflects the
 * CURRENT tool language (python vs javascript), read from the sidecar manifest.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";

import { toolsApi } from "@/lib/api-ai";
import {
  DOCS_LABELS,
  libraryDescription,
  pickToolLang,
  pickToolScript,
  toolGuideSections,
} from "@/lib/toolDocs";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const btnCls =
  "inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-sm hover:bg-muted disabled:opacity-50";
const inputCls = "rounded-md border border-border bg-background px-2 py-1.5 text-sm";

export function ToolDocsDialog({ language, onClose }: { language: string; onClose: () => void }) {
  const { i18n } = useTranslation();
  const lang = pickToolLang(i18n.language);
  const L = DOCS_LABELS[lang];
  const [filter, setFilter] = useState("");
  const q = useQuery({ queryKey: ["ai", "tools", "languages"], queryFn: () => toolsApi.languages() });

  const sections = toolGuideSections(lang, pickToolScript(language));
  const langDef = q.data?.languages.find((l) => l.name === language);
  const libs = langDef?.libraries ?? [];
  const shown = libs.filter((l) => l.toLowerCase().includes(filter.toLowerCase()));

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">{L.title}</DialogTitle>
        </DialogHeader>
        <DialogBody className="max-h-[70vh] space-y-5 overflow-y-auto">
          {/* Guide */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-muted-foreground">{L.guide}</h3>
            {sections.map((s) => (
              <div key={s.title}>
                <p className="text-sm font-medium">{s.title}</p>
                <p className="mt-0.5 text-sm text-muted-foreground">{s.body}</p>
                {s.code && (
                  <pre className="mt-1.5 overflow-x-auto rounded-md border border-border bg-muted/40 p-2 font-mono text-xs">
                    {s.code}
                  </pre>
                )}
              </div>
            ))}
          </section>

          {/* Libraries */}
          <section>
            <div className="mb-2 flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-muted-foreground">
                {L.libraries} {libs.length > 0 && `(${libs.length})`}
              </h3>
              {libs.length > 0 && (
                <input
                  className={`${inputCls} h-7 w-40 py-1 text-xs`}
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder={L.search}
                />
              )}
            </div>
            {q.isLoading ? (
              <p className="text-sm text-muted-foreground">…</p>
            ) : !q.data?.sandbox_configured ? (
              <p className="text-sm text-muted-foreground">Sandbox non configurata.</p>
            ) : (
              <ul className="divide-y divide-border/60 rounded-md border border-border">
                {shown.map((name) => (
                  <li key={name} className="flex items-baseline gap-3 px-2.5 py-1.5">
                    <span className="w-40 shrink-0 font-mono text-xs">{name}</span>
                    <span className="text-xs text-muted-foreground">{libraryDescription(name, lang)}</span>
                  </li>
                ))}
                {shown.length === 0 && (
                  <li className="px-2.5 py-2 text-xs text-muted-foreground">{L.noMatch}</li>
                )}
              </ul>
            )}
          </section>
        </DialogBody>
        <DialogFooter>
          <button type="button" className={btnCls} onClick={onClose}>{L.close}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
